import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useGuideSurface } from '@runcofactor/guide';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { RowSelectionState } from '@tanstack/react-table';
import { API_URL } from '../../config';
import { toUrlId } from '../../lib/prefixed-ids';
import { useRowLinkBuilder } from '../../lib/useRowLink';
import {
  Page as PageLayout,
  PageHeader,
  Card,
  Flex,
  Text,
  Button,
  LoadingOrbit,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  useToast,
  MenuTrigger,
  Menu,
  MenuItem,
  TagPicker,
  UnderlineTabs,
  UnderlineTab,
  BulkActionBar,
  type SelectOption,
  type TagOption,
  type StatusTimelineEntry,
  type BulkAction,
} from '@chatsdet/ui';
import {
  Plus,
  FlowArrow,
  Trash,
  UploadSimple,
  Play,
  Plant,
  Broom,
  Tag,
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { useEligibilityModel } from '../../hooks/useEligibilityModel';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useWorkflowMutations } from '../../hooks/useWorkflowMutations';
import {
  useWorkflowsQuery,
  useGroupedWorkflowsQuery,
  useWorkflowTagOptions,
  type WorkflowListItemWithDetails,
  type WorkflowStatus,
} from '../../hooks/useWorkflows';
import { useImportStream } from '../../hooks/useImportStream';
import { useActiveImport } from '../../hooks/useActiveImport';
import { useWorkflowFilters } from '../components/workflows/WorkflowFilters';
import { useCreateTagMutation, type TagColor } from '../../hooks/useTags';
import { useFlowFavorites } from '../../hooks/useFlowFavorites';
import { useFlowViewPrefs, type FlowsViewMode } from '../../hooks/useUserSettings';
import { ImportErrorsDrawer } from '../components/ImportErrorsDrawer';
import { ImportDialog } from '../components/workflows/ImportDialog';
import { useOrgMembers } from '../../hooks/useOrgMembers';
import type { FlowsSearchParams } from '../../router';
import { isExpectedClientApiError, toApiError } from '../../lib/api-error';
import { DEFAULT_WORKFLOW_NAME } from './FlowDesigner/constants';
import {
  FlowFilterBar,
  FlowListImportBanner,
  FlowTableView,
  FlowTagSections,
  FlowViewControls,
} from './flow-list/components';
import type { FlowActionHandlers, FlowActionTarget, LatestRunInfo } from './flow-list/components';
import type {
  OpenIssuesByWorkflow,
  PropertyListItem,
  WorkflowRunsByWorkflow,
} from './flow-list/types';
import {
  fetchFlowListOpenIssues,
  fetchFlowListProperties,
  fetchFlowListRecentRuns,
} from './flow-list/api';
import {
  formatWorkflowStatus,
  getWorkflowRunTimestamp,
  isRunnableFromFlowList,
  workflowRunStatusTone,
} from './flow-list/utils';
import {
  buildRunTimelineEntries,
  findLastDiagnosedCategoryInStreak,
} from '../../features/workflows/runTimeline';
import type { FailureDiagnosticCategory } from '@chatsdet/types/issues';
import { CreateSeedScriptDialog } from '../components/CreateSeedScriptDialog';
import { CreateTeardownScriptDialog } from '../components/CreateTeardownScriptDialog';
import { getRecentRunWorkflowIds } from './flow-list/recentRuns';

export function FlowList() {
  const navigate = useNavigate();
  const buildHref = useRowLinkBuilder();
  const flowHref = React.useCallback(
    (id: string) =>
      buildHref({ to: '/flows/$workflowId', params: { workflowId: toUrlId('wf', id) } }),
    [buildHref]
  );
  const searchParams = useSearch({ from: '/flows' }) as FlowsSearchParams;

  // The Seed/Teardown tabs reuse this whole page (table, filters,
  // views, bulk actions) scoped to a single flow type. `scriptKind` drives the
  // tab-aware chrome (create CTA, empty state); `effectiveSearchParams`
  // injects the implicit flowType scope into every data/scope consumer below.
  // The Flows tab keeps whatever flowType the user picked (default: server
  // excludes seed/teardown).
  const scriptKind: 'seed' | 'teardown' | null =
    searchParams.tab === 'seed-scripts'
      ? 'seed'
      : searchParams.tab === 'teardown-scripts'
        ? 'teardown'
        : null;
  const effectiveSearchParams = React.useMemo<FlowsSearchParams>(
    () => ({
      ...searchParams,
      flowType: scriptKind ? [scriptKind] : searchParams.flowType,
    }),
    [searchParams, scriptKind]
  );

  const exportUrl = React.useMemo(() => {
    const params = new URLSearchParams();
    params.set('format', 'csv');
    if (effectiveSearchParams.search) params.set('search', effectiveSearchParams.search);
    if (effectiveSearchParams.status && effectiveSearchParams.status.length > 0) {
      params.set('status', effectiveSearchParams.status.join(','));
    }
    if (effectiveSearchParams.health && effectiveSearchParams.health.length > 0) {
      params.set('health', effectiveSearchParams.health.join(','));
    }
    if (effectiveSearchParams.promotion && effectiveSearchParams.promotion.length > 0) {
      params.set('promotion', effectiveSearchParams.promotion.join(','));
    }
    if (typeof effectiveSearchParams.eligible === 'boolean') {
      params.set('eligible', String(effectiveSearchParams.eligible));
    }
    if (typeof effectiveSearchParams.paused === 'boolean') {
      params.set('paused', String(effectiveSearchParams.paused));
    }
    if (effectiveSearchParams.lastRunStatus && effectiveSearchParams.lastRunStatus.length > 0) {
      params.set('lastRunStatus', effectiveSearchParams.lastRunStatus.join(','));
    }
    if (effectiveSearchParams.tagIds && effectiveSearchParams.tagIds.length > 0) {
      params.set('tagIds', effectiveSearchParams.tagIds.join(','));
    }
    if (effectiveSearchParams.dateFrom) params.set('dateFrom', effectiveSearchParams.dateFrom);
    if (effectiveSearchParams.dateTo) params.set('dateTo', effectiveSearchParams.dateTo);
    if (effectiveSearchParams.flowType && effectiveSearchParams.flowType.length > 0) {
      params.set('flowType', effectiveSearchParams.flowType.join(','));
    }
    if (effectiveSearchParams.createdByIds && effectiveSearchParams.createdByIds.length > 0) {
      params.set('createdByIds', effectiveSearchParams.createdByIds.join(','));
    }
    if (effectiveSearchParams.favorites) params.set('favoritesOnly', 'true');
    if (effectiveSearchParams.sortBy) params.set('sortBy', effectiveSearchParams.sortBy);
    if (effectiveSearchParams.sortOrder) params.set('sortOrder', effectiveSearchParams.sortOrder);
    return `${API_URL}/workflows?${params.toString()}`;
  }, [effectiveSearchParams]);

  const { user } = useAuth();
  const currentOrgId = user?.orgId ?? null;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Local state
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [seedDialogOpen, setSeedDialogOpen] = React.useState(false);
  const [teardownDialogOpen, setTeardownDialogOpen] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState(searchParams.search ?? '');
  const [workflowToArchive, setWorkflowToArchive] = React.useState<FlowActionTarget | null>(null);
  const [archiveIntentTimestamp, setArchiveIntentTimestamp] = React.useState<number | null>(null);

  // View mode: URL param wins (shareable), otherwise the locally persisted
  // preference (grouped by default). Explicit switches persist + write the URL.
  const {
    viewMode: persistedViewMode,
    setViewMode,
    isSectionCollapsed,
    setSectionCollapsed,
  } = useFlowViewPrefs(currentOrgId);
  // Per-org eligibility model: ON = derived eligibility + Pause/Unpause; OFF = promotion model.
  const eligibilityModel = useEligibilityModel();
  const view: FlowsViewMode = searchParams.view ?? persistedViewMode;

  // Import streaming state
  const [activeImportId, setActiveImportId] = React.useState<string | null>(null);
  const [showErrorsDrawer, setShowErrorsDrawer] = React.useState(false);

  // Check for active imports on mount (for reconnection after refresh)
  const {
    activeImport,
    isActive: hasActiveImport,
    sourceType: importSourceType,
  } = useActiveImport();

  // Auto-reconnect to active import on mount
  React.useEffect(() => {
    if (hasActiveImport && activeImport && !activeImportId) {
      setActiveImportId(activeImport.id);
    }
  }, [hasActiveImport, activeImport, activeImportId]);

  // Bulk selection state — dual-mode: 'page' selects specific IDs, 'all' selects everything minus exclusions
  const [selectionScope, setSelectionScope] = React.useState<'page' | 'all' | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [deselectedIds, setDeselectedIds] = React.useState<Set<string>>(new Set());
  const [selectAllSnapshot, setSelectAllSnapshot] = React.useState<FlowsSearchParams | null>(null);

  // Bulk tag dialog state
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = React.useState(false);
  const [tagsToAdd, setTagsToAdd] = React.useState<TagOption[]>([]);

  // Bulk run confirmation state
  const [bulkRunConfirmOpen, setBulkRunConfirmOpen] = React.useState(false);
  const [activeRunCount, setActiveRunCount] = React.useState(0);

  // Sync search input with URL params
  React.useEffect(() => {
    setSearchInput(searchParams.search ?? '');
  }, [searchParams.search]);

  // URL update helper
  const updateSearch = React.useCallback(
    (updates: Partial<FlowsSearchParams>) => {
      navigate({
        to: '/flows',
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, ...updates };
          // Clean up undefined values
          for (const key of Object.keys(next)) {
            if (next[key] === undefined) {
              delete next[key];
            }
          }
          return next as FlowsSearchParams;
        },
      });
    },
    [navigate]
  );

  // Debounced search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (searchParams.search ?? '')) {
        updateSearch({
          search: searchInput.trim() || undefined,
          page: 1,
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchParams.search, updateSearch]);

  // Breadcrumbs
  const breadcrumbs = React.useMemo(
    () => [
      { label: 'Home', onPress: () => navigate({ to: '/' }) },
      { label: 'Flows', onPress: () => navigate({ to: '/flows' }) },
    ],
    [navigate]
  );
  useBreadcrumbs(breadcrumbs);

  // Data queries — exactly one of the two list queries runs per view mode
  // (the grouped view uses the uncapped skinny query)
  const workflowsQuery = useWorkflowsQuery(effectiveSearchParams, view === 'table');
  const groupedQuery = useGroupedWorkflowsQuery(effectiveSearchParams, view !== 'table');
  const { tags, isLoading: tagsLoading } = useWorkflowTagOptions();

  // Per-user favorites (optimistic star toggle)
  const { toggleFavorite } = useFlowFavorites();

  const handleViewChange = React.useCallback(
    (next: FlowsViewMode) => {
      setViewMode(next);
      // Reset pagination — grouped mode ignores it, and a stale deep page
      // coming back to the table view would render empty.
      updateSearch({ view: next, page: 1 });
    },
    [setViewMode, updateSearch]
  );

  const propertiesQuery = useQuery<{ items: PropertyListItem[] }>({
    queryKey: ['properties', currentOrgId],
    enabled: !!currentOrgId,
    queryFn: () => fetchFlowListProperties(),
  });

  const propertyOptions: SelectOption[] = React.useMemo(
    () =>
      (propertiesQuery.data?.items ?? []).map((property) => ({
        id: property.id,
        label: property.name,
      })),
    [propertiesQuery.data?.items]
  );

  const showPropertySelect = (propertiesQuery.data?.items ?? []).length > 1;

  const membersQuery = useOrgMembers();
  const membersByUserId = React.useMemo(() => {
    const map = new Map<string, { displayName: string | null; primaryEmail: string | null }>();
    for (const m of membersQuery.data?.items ?? []) {
      map.set(m.userId, { displayName: m.displayName, primaryEmail: m.primaryEmail });
    }
    return map;
  }, [membersQuery.data?.items]);

  const authorOptions = React.useMemo(
    () =>
      (membersQuery.data?.items ?? []).map((m) => ({
        id: m.userId,
        label: m.displayName || m.primaryEmail || 'Unknown',
      })),
    [membersQuery.data?.items]
  );

  const visibleWorkflowIds = React.useMemo(() => {
    const workflows =
      view === 'table' ? (workflowsQuery.data?.items ?? []) : (groupedQuery.data?.data ?? []);
    return workflows.map((workflow) => workflow.id);
  }, [groupedQuery.data?.data, view, workflowsQuery.data?.items]);
  const recentRunWorkflowIds = React.useMemo(
    () => getRecentRunWorkflowIds(visibleWorkflowIds),
    [visibleWorkflowIds]
  );

  // Fetch recent workflow runs (not test runs) for timeline display
  const workflowRunsQuery = useQuery<WorkflowRunsByWorkflow>({
    queryKey: ['workflow-runs-recent', currentOrgId, recentRunWorkflowIds],
    enabled: !!currentOrgId && recentRunWorkflowIds.length > 0,
    queryFn: () => fetchFlowListRecentRuns(recentRunWorkflowIds),
  });

  // Open issues persistently linked to each visible flow — drives the "N
  // issues" chip and suppresses the diagnosed-category icon once every filed
  // issue for a software_bug/app_changed streak has been closed.
  const openIssuesQuery = useQuery<OpenIssuesByWorkflow>({
    queryKey: ['workflow-open-issues', currentOrgId, recentRunWorkflowIds],
    enabled: !!currentOrgId && recentRunWorkflowIds.length > 0,
    queryFn: () => fetchFlowListOpenIssues(recentRunWorkflowIds),
  });
  const openIssuesByWorkflow = openIssuesQuery.data ?? {};

  const workflowTimelineEntries = React.useMemo<Record<string, StatusTimelineEntry[]>>(() => {
    const runsByWorkflow = workflowRunsQuery.data ?? {};
    // Names come from whichever list query is active (table page or grouped set)
    const nameMap = new Map<string, string>();
    for (const w of workflowsQuery.data?.items ?? []) nameMap.set(w.id, w.name);
    for (const w of groupedQuery.data?.data ?? []) nameMap.set(w.id, w.name);
    const entries: Record<string, StatusTimelineEntry[]> = {};

    for (const [workflowId, runs] of Object.entries(runsByWorkflow)) {
      const workflowName = nameMap.get(workflowId) ?? 'Workflow';
      entries[workflowId] = buildRunTimelineEntries(runs, workflowName);
    }

    return entries;
  }, [workflowRunsQuery.data, workflowsQuery.data?.items, groupedQuery.data?.data]);

  // Diagnosed category (if any) for the newest diagnosed run within each
  // workflow's current failure streak — rendered as a second icon next to the
  // health icon in FlowRecentRuns. Parallel to workflowTimelineEntries rather
  // than folded into StatusTimelineEntry, which is a generic shared type also
  // used outside the workflow-run context.
  const diagnosedCategoryByWorkflow = React.useMemo<
    Record<string, FailureDiagnosticCategory | null>
  >(() => {
    const runsByWorkflow = workflowRunsQuery.data ?? {};
    const categories: Record<string, FailureDiagnosticCategory | null> = {};
    for (const [workflowId, runs] of Object.entries(runsByWorkflow)) {
      const category = findLastDiagnosedCategoryInStreak(runs);
      // The icon means "this failure streak has a diagnosed issue to look
      // at" — once every issue filed for a software_bug/app_changed verdict
      // is closed (dismissed as not-a-bug or resolved), there's nothing left
      // to surface, so hide it. Other categories aren't auto-filed and keep
      // their icon regardless of open-issue count. Only suppress once the
      // open-issues fetch has actually resolved, to avoid flashing the icon
      // off while that request is still in flight.
      const suppressed =
        (category === 'software_bug' || category === 'app_changed') &&
        openIssuesQuery.data !== undefined &&
        (openIssuesByWorkflow[workflowId]?.length ?? 0) === 0;
      categories[workflowId] = suppressed ? null : category;
    }
    return categories;
  }, [workflowRunsQuery.data, openIssuesByWorkflow, openIssuesQuery.data]);

  // Import streaming hook
  const importStream = useImportStream(activeImportId, {
    onWorkflowCreated: () => {
      // Refresh workflows list when a new workflow is created
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onComplete: (state) => {
      if (state.failedRows > 0) {
        toast.warning(
          `Import completed with ${state.failedRows} failed row${state.failedRows === 1 ? '' : 's'}`
        );
      } else {
        toast.success('AI finished building workflows');
      }
      // Refresh workflows to pick up published status changes from auto-run
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
      // Keep banner visible when there are errors so user can review them;
      // auto-dismiss only on full success
      if (state.failedRows === 0) {
        setTimeout(() => setActiveImportId(null), 5000);
      }
    },
    onAutoRunStarted: (count) => {
      toast.info(`Running ${count} imported workflow${count === 1 ? '' : 's'}...`);
      // Refresh timeline so new runs appear
      void queryClient.invalidateQueries({ queryKey: ['workflow-runs-recent', currentOrgId] });
    },
  });

  // Show banner when streaming or have active import
  const showImportBanner =
    activeImportId && importSourceType === 'csv' && importStream.status !== 'idle';

  // Mutations
  const uploadImportMutation = useMutation<
    { importId: string },
    Error,
    { file: File; defaultPropertyId?: string | null }
  >({
    mutationFn: async ({ file, defaultPropertyId }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (defaultPropertyId) {
        formData.append('defaultPropertyId', defaultPropertyId);
      }
      const response = await fetch(`${API_URL}/workflows/imports`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to upload CSV');
      }
      return response.json();
    },
    onMutate: () => {
      // Close dialog immediately
      setImportDialogOpen(false);
    },
    onSuccess: (data) => {
      toast.success('AI building workflows');
      // Start streaming from the new import
      setActiveImportId(data.importId);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to upload CSV.');
    },
  });

  const runTestMutation = useMutation<
    { runId: string; streamId: string },
    Error,
    { workflowId: string; workflowName: string }
  >({
    mutationFn: async ({ workflowId }) => {
      const response = await fetch(`${API_URL}/workflows/${workflowId}/test-run`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start test run');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Test run started');
      // Refresh the timeline to show the new running test
      void queryClient.invalidateQueries({ queryKey: ['workflow-runs-recent', currentOrgId] });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : null;
      toast.error(message || 'Failed to start test run');
    },
  });

  const { createWorkflowMutation, archiveWorkflowMutation, forkWorkflowMutation } =
    useWorkflowMutations({
      currentOrgId,
      workflowId: null,
      onWorkflowCreated: (workflow) => {
        navigate({
          to: '/flows/$workflowId',
          params: { workflowId: toUrlId('wf', workflow.id) },
        });
      },
    });

  // Bulk archive (soft-delete). Archived flows are hidden from lists and
  // skipped by test suites; they remain reachable by id for old run permalinks.
  const bulkArchiveMutation = useMutation<
    { ok: boolean; archived: number; skipped: number },
    Error,
    Record<string, unknown>
  >({
    mutationFn: async (payload) => {
      const response = await fetch(`${API_URL}/workflows/bulk`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to archive workflows');
      }
      return response.json();
    },
    onSuccess: (data) => {
      const base = `Archived ${data.archived} workflow${data.archived === 1 ? '' : 's'}`;
      toast.success(data.skipped > 0 ? `${base} (${data.skipped} already archived)` : base);
      clearSelection();
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to archive workflows', {
        reportError: !isExpectedClientApiError(error),
      });
    },
  });

  const bulkRunMutation = useMutation<
    {
      ok: boolean;
      suiteId: string;
      streamId: string;
      jobs: Array<{ workflowId: string; jobId: string }>;
      enqueued?: number;
      skipped?: number;
    },
    Error,
    Record<string, unknown>
  >({
    mutationFn: async (payload) => {
      const response = await fetch(`${API_URL}/workflows/bulk-run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start test runs');
      }
      return response.json();
    },
    onSuccess: (data) => {
      const skipped = data.skipped ?? 0;
      const startedMessage = `Started ${data.jobs.length} test run${data.jobs.length === 1 ? '' : 's'}`;
      toast.success(skipped > 0 ? `${startedMessage} (${skipped} skipped)` : startedMessage);
      clearSelection();
      void queryClient.invalidateQueries({ queryKey: ['workflow-runs-recent', currentOrgId] });
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start test runs');
    },
  });

  const updateWorkflowStatusMutation = useMutation<
    { workflow: { id: string } },
    Error,
    { workflowId: string; status: WorkflowStatus; workflowName: string }
  >({
    mutationFn: async ({ workflowId, status }) => {
      // Approval goes through the dedicated endpoint so approvedBy/approvedAt
      // are recorded; reverting to draft is a plain status update.
      const response =
        status === 'approved'
          ? await fetch(`${API_URL}/workflows/${workflowId}/approve`, {
              method: 'POST',
              credentials: 'include',
            })
          : await fetch(`${API_URL}/workflows/${workflowId}`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ status }),
            });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update workflow status');
      }
      return response.json();
    },
    onSuccess: (_data, { workflowName, status }) => {
      toast.success(`"${workflowName}" set to ${formatWorkflowStatus(status)}`);
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update workflow status');
    },
  });

  const setWorkflowPausedMutation = useMutation<
    { workflow: { id: string } },
    Error,
    { workflowId: string; paused: boolean; workflowName: string }
  >({
    mutationFn: async ({ workflowId, paused }) => {
      const action = paused ? 'pause' : 'unpause';
      const response = await fetch(`${API_URL}/workflows/${workflowId}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update workflow');
      }
      return response.json();
    },
    onSuccess: (_data, { workflowName, paused }) => {
      toast.success(
        paused
          ? `"${workflowName}" paused — excluded from suite runs`
          : `"${workflowName}" resumed — it will run when test suites run`
      );
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update workflow');
    },
  });

  // Promotion model (eligibility flag OFF): promote/pause a single flow.
  const setWorkflowPromotionMutation = useMutation<
    { workflow: { id: string } },
    Error,
    { workflowId: string; promotion: 'promoted' | 'paused'; workflowName: string }
  >({
    mutationFn: async ({ workflowId, promotion }) => {
      const action = promotion === 'promoted' ? 'promote' : 'pause';
      const response = await fetch(`${API_URL}/workflows/${workflowId}/${action}`, {
        method: 'POST',
        credentials: 'include',
        ...(promotion === 'promoted'
          ? {
              headers: { 'content-type': 'application/json' },
              // List-level promote skips the run gate (force); the approval
              // gate is enforced server-side and can never be forced.
              body: JSON.stringify({ force: true }),
            }
          : {}),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to update workflow promotion');
      }
      return response.json();
    },
    onSuccess: (_data, { workflowName, promotion }) => {
      toast.success(
        promotion === 'promoted'
          ? `"${workflowName}" promoted — it will run when test suites run`
          : `"${workflowName}" paused — excluded from suite runs`
      );
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update workflow promotion', {
        reportError: !isExpectedClientApiError(error),
      });
    },
  });

  const bulkStatusMutation = useMutation<
    { ok: boolean; updated: number; skipped: number },
    Error,
    { status: WorkflowStatus } & Record<string, unknown>
  >({
    mutationFn: async (payload) => {
      const response = await fetch(`${API_URL}/workflows/bulk-status`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to update workflows');
      }
      return response.json();
    },
    onSuccess: (data, { status }) => {
      toast.success(
        `Updated ${data.updated} workflow${data.updated === 1 ? '' : 's'} to ${formatWorkflowStatus(status)}${data.skipped > 0 ? ` (${data.skipped} skipped)` : ''}`
      );
      clearSelection();
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update workflows', {
        reportError: !isExpectedClientApiError(error),
      });
    },
  });

  const bulkPauseMutation = useMutation<
    { ok: boolean; updated: number; skipped: number; alreadyInState: number; failed: number },
    Error,
    { paused: boolean } & Record<string, unknown>
  >({
    mutationFn: async (payload) => {
      const response = await fetch(`${API_URL}/workflows/bulk-pause`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to update workflows');
      }
      return response.json();
    },
    onSuccess: (data, { paused }) => {
      const verb = paused ? 'Paused' : 'Resumed';
      const notes = [
        data.alreadyInState > 0 ? `${data.alreadyInState} already in that state` : null,
        data.failed > 0 ? `${data.failed} failed` : null,
      ].filter(Boolean);
      const message = `${verb} ${data.updated} workflow${data.updated === 1 ? '' : 's'}${
        notes.length > 0 ? ` (${notes.join(', ')})` : ''
      }`;
      if (data.failed > 0) {
        toast.error(message);
      } else {
        toast.success(message);
      }
      clearSelection();
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update workflows', {
        reportError: !isExpectedClientApiError(error),
      });
    },
  });

  // Promotion model (eligibility flag OFF): bulk promote/pause.
  const bulkPromotionMutation = useMutation<
    { ok: boolean; updated: number; skipped: number; alreadyInState: number; failed: number },
    Error,
    { promotion: 'promoted' | 'paused' } & Record<string, unknown>
  >({
    mutationFn: async (payload) => {
      const response = await fetch(`${API_URL}/workflows/bulk-promotion`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to update workflows');
      }
      return response.json();
    },
    onSuccess: (data, { promotion }) => {
      const verb = promotion === 'promoted' ? 'Promoted' : 'Paused';
      const notes = [
        data.skipped > 0 ? `${data.skipped} need approval first` : null,
        data.alreadyInState > 0 ? `${data.alreadyInState} already in that state` : null,
        data.failed > 0 ? `${data.failed} failed` : null,
      ].filter(Boolean);
      const message = `${verb} ${data.updated} workflow${data.updated === 1 ? '' : 's'}${
        notes.length > 0 ? ` (${notes.join(', ')})` : ''
      }`;
      if (data.failed > 0) {
        toast.error(message);
      } else {
        toast.success(message);
      }
      clearSelection();
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update workflows', {
        reportError: !isExpectedClientApiError(error),
      });
    },
  });

  const bulkTagMutation = useMutation<
    { ok: boolean; updated: number },
    Error,
    Record<string, unknown>
  >({
    mutationFn: async (payload) => {
      const response = await fetch(`${API_URL}/workflows/bulk-tags`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to add tags');
      }
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(`Added tags to ${data.updated} workflow${data.updated === 1 ? '' : 's'}`);
      clearSelection();
      setBulkTagDialogOpen(false);
      setTagsToAdd([]);
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
      void queryClient.invalidateQueries({ queryKey: ['workflow-tags-batch'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add tags', {
        reportError: !isExpectedClientApiError(error),
      });
    },
  });

  const createTagMutation = useCreateTagMutation();

  const handleCreateTag = React.useCallback(
    async (name: string, color: TagColor): Promise<TagOption> => {
      const result = await createTagMutation.mutateAsync({ name, color });
      return {
        id: result.tag.id,
        name: result.tag.name,
        color: result.tag.color,
      };
    },
    [createTagMutation]
  );

  // Filters
  const {
    filterDialogTrigger,
    activeFilterChips,
    handleFilterRemove,
    handleResetFilters,
    hasActiveFilters,
  } = useWorkflowFilters({
    searchParams,
    onFilterChange: updateSearch,
    tags,
    authors: authorOptions,
    authorsLoading: membersQuery.isLoading,
  });

  // Latest run per workflow — for the "Last run" column
  const latestRunByWorkflow = React.useMemo<Record<string, LatestRunInfo | undefined>>(() => {
    const map: Record<string, LatestRunInfo | undefined> = {};
    const runsByWorkflow = workflowRunsQuery.data ?? {};
    for (const [workflowId, runs] of Object.entries(runsByWorkflow)) {
      const latest = runs[0];
      if (!latest) continue;
      const ts = getWorkflowRunTimestamp(latest);
      if (!ts) continue;
      // Still-running (or otherwise unfinished) runs must show "—", never a
      // growing number, so duration only resolves once both ends are known.
      const elapsed =
        latest.startedAt && latest.finishedAt
          ? Date.parse(latest.finishedAt) - Date.parse(latest.startedAt)
          : NaN;
      const durationMs = Number.isFinite(elapsed) && elapsed >= 0 ? elapsed : null;
      map[workflowId] = { ts, tone: workflowRunStatusTone[latest.status], durationMs };
    }
    return map;
  }, [workflowRunsQuery.data]);

  // Per-flow action callbacks shared by the table's actions column and (later)
  // grouped-view cards. Mutations live here; renderers stay presentational.
  const actionHandlers = React.useMemo<FlowActionHandlers>(
    () => ({
      onRunTest: (workflow) => {
        // Explicit runs work regardless of eligibility/pause; only system states
        // (generating/building/diagnostic) aren't runnable.
        if (!isRunnableFromFlowList(workflow.status)) {
          toast.error('This workflow is still being built and cannot run yet');
          return;
        }
        runTestMutation.mutate({
          workflowId: workflow.id,
          workflowName: workflow.name,
        });
      },
      onFork: (workflowId) => {
        forkWorkflowMutation.mutate(workflowId);
      },
      onSetStatus: (workflow, status) => {
        updateWorkflowStatusMutation.mutate({
          workflowId: workflow.id,
          status,
          workflowName: workflow.name || 'Untitled Workflow',
        });
      },
      onSetPaused: (workflow, paused) => {
        setWorkflowPausedMutation.mutate({
          workflowId: workflow.id,
          paused,
          workflowName: workflow.name || 'Untitled Workflow',
        });
      },
      onSetPromotion: (workflow, promotion) => {
        setWorkflowPromotionMutation.mutate({
          workflowId: workflow.id,
          promotion,
          workflowName: workflow.name || 'Untitled Workflow',
        });
      },
      onArchive: (workflow) => {
        setWorkflowToArchive(workflow);
        setArchiveIntentTimestamp(Date.now());
      },
      isRunPending: runTestMutation.isPending,
      isStatusPending:
        updateWorkflowStatusMutation.isPending ||
        setWorkflowPausedMutation.isPending ||
        setWorkflowPromotionMutation.isPending,
      isForkPending: forkWorkflowMutation.isPending,
    }),
    [
      runTestMutation,
      forkWorkflowMutation,
      updateWorkflowStatusMutation,
      setWorkflowPausedMutation,
      setWorkflowPromotionMutation,
      toast,
    ]
  );

  // Handlers
  const handleCreateFlow = React.useCallback(async () => {
    try {
      const result = await createWorkflowMutation.mutateAsync({
        name: DEFAULT_WORKFLOW_NAME,
        status: 'draft',
      });

      const newWorkflowId = result?.workflow?.id;
      if (newWorkflowId) {
        navigate({
          to: '/flows/$workflowId',
          params: { workflowId: toUrlId('wf', newWorkflowId) },
        });
      }
    } catch (error) {
      console.error('Failed to create workflow', error);
    }
  }, [createWorkflowMutation, navigate]);

  const createFlowGuide = useGuideSurface<HTMLButtonElement>({
    id: 'flows.create',
    role: 'button',
    label: 'Create a new flow',
    describe: () => ({ disabled: createWorkflowMutation.isPending }),
  });

  const handleOpenFlow = React.useCallback(
    (id: string, modifiers?: { metaKey: boolean; ctrlKey: boolean }) => {
      const workflowId = toUrlId('wf', id);
      if (modifiers?.metaKey || modifiers?.ctrlKey) {
        window.open(`/flows/${workflowId}`, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate({
        to: '/flows/$workflowId',
        params: { workflowId },
      });
    },
    [navigate]
  );

  const handleRowClick = React.useCallback(
    (workflow: WorkflowListItemWithDetails, modifiers?: { metaKey: boolean; ctrlKey: boolean }) => {
      handleOpenFlow(workflow.id, modifiers);
    },
    [handleOpenFlow]
  );

  const handleConfirmArchive = React.useCallback(async () => {
    if (!workflowToArchive) {
      return;
    }
    const timeToArchiveMs = archiveIntentTimestamp
      ? Date.now() - archiveIntentTimestamp
      : undefined;
    try {
      await archiveWorkflowMutation.mutateAsync({
        id: workflowToArchive.id,
        archivedFrom: 'dashboard',
        confirmationMethod: 'modal_confirm',
        timeToArchiveMs,
      });
      toast.success('Flow archived');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to archive flow';
      toast.error(message, {
        reportError: !isExpectedClientApiError(error),
      });
    } finally {
      setWorkflowToArchive(null);
      setArchiveIntentTimestamp(null);
    }
  }, [archiveWorkflowMutation, workflowToArchive, archiveIntentTimestamp, toast]);

  const handlePageChange = React.useCallback(
    (page: number) => {
      updateSearch({ page });
    },
    [updateSearch]
  );

  // Server-side column sorting. Cycling back to unsorted clears both params so
  // the API falls back to the default createdAt-desc ordering.
  const handleSortChange = React.useCallback(
    (nextSortBy?: string, nextSortOrder?: 'asc' | 'desc') => {
      updateSearch({ sortBy: nextSortBy, sortOrder: nextSortOrder, page: 1 });
    },
    [updateSearch]
  );

  // ---------------------------------------------------------------------------
  // Cross-page selection logic (follows Pages.tsx pattern)
  // ---------------------------------------------------------------------------

  const totalItems = workflowsQuery.data?.pagination?.totalItems ?? 0;
  const pageItems = workflowsQuery.data?.items ?? [];

  // Compute TanStack rowSelection from our dual-mode state
  const rowSelection = React.useMemo<RowSelectionState>(() => {
    const state: RowSelectionState = {};
    if (selectionScope === 'all') {
      for (const item of pageItems) {
        if (!deselectedIds.has(item.id)) {
          state[item.id] = true;
        }
      }
    } else {
      for (const id of selectedIds) {
        state[id] = true;
      }
    }
    return state;
  }, [selectionScope, pageItems, selectedIds, deselectedIds]);

  // Selection metrics
  const deselectedCount = selectionScope === 'all' ? deselectedIds.size : 0;
  const selectedCount =
    selectionScope === 'all' ? Math.max(0, totalItems - deselectedCount) : selectedIds.size;
  const canSelectAllResults =
    selectionScope !== 'all' && selectedCount > 0 && totalItems > selectedCount;
  const resolvedSelectionMode: 'page' | 'all' = selectionScope === 'all' ? 'all' : 'page';

  // Handle DataTable checkbox changes
  const handleRowSelectionChange = React.useCallback(
    (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const newState =
        typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

      if (selectionScope === 'all') {
        // In "all" mode, track deselections
        const nextDeselected = new Set(deselectedIds);
        for (const item of pageItems) {
          if (newState[item.id]) {
            nextDeselected.delete(item.id);
          } else {
            nextDeselected.add(item.id);
          }
        }
        // If everything is deselected, exit "all" mode
        if (nextDeselected.size >= totalItems) {
          setSelectionScope(null);
          setSelectedIds(new Set());
          setDeselectedIds(new Set());
          setSelectAllSnapshot(null);
        } else {
          setDeselectedIds(nextDeselected);
        }
      } else {
        // In "page" mode, track selections
        const nextSelected = new Set(selectedIds);
        // Remove IDs from current page that are no longer selected
        for (const item of pageItems) {
          if (newState[item.id]) {
            nextSelected.add(item.id);
          } else {
            nextSelected.delete(item.id);
          }
        }
        if (nextSelected.size === 0) {
          setSelectionScope(null);
        } else if (selectionScope === null) {
          setSelectionScope('page');
        }
        setSelectedIds(nextSelected);
      }
    },
    [rowSelection, selectionScope, deselectedIds, selectedIds, pageItems, totalItems]
  );

  // Select all results across all pages
  const handleSelectAllResults = React.useCallback(() => {
    if (!canSelectAllResults) return;
    setSelectionScope('all');
    setSelectedIds(new Set());
    setDeselectedIds(new Set());
    setSelectAllSnapshot({ ...effectiveSearchParams });
  }, [canSelectAllResults, effectiveSearchParams]);

  // Clear selection
  const clearSelection = React.useCallback(() => {
    setSelectionScope(null);
    setSelectedIds(new Set());
    setDeselectedIds(new Set());
    setSelectAllSnapshot(null);
  }, []);

  // Extract filter fields from search params (excludes page/pageSize/tab)
  const extractFilters = React.useCallback(
    (params: FlowsSearchParams) => ({
      search: params.search,
      status: params.status,
      health: params.health,
      promotion: params.promotion,
      eligible: params.eligible,
      paused: params.paused,
      lastRunStatus: params.lastRunStatus,
      tagIds: params.tagIds,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      flowType: params.flowType,
      favoritesOnly: params.favorites === true || undefined,
    }),
    []
  );

  // Auto-clear selection when filters change. Keyed on effectiveSearchParams so
  // switching tabs (which changes the injected flowType) also clears selection.
  const filterKey = JSON.stringify(extractFilters(effectiveSearchParams));
  React.useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Build bulk payload based on selection mode
  const buildBulkPayload = React.useCallback((): Record<string, unknown> => {
    if (selectionScope === 'all') {
      return {
        selectAll: true,
        filters: extractFilters(selectAllSnapshot ?? effectiveSearchParams),
        excludeIds: deselectedIds.size > 0 ? Array.from(deselectedIds) : undefined,
      };
    }
    return { ids: Array.from(selectedIds) };
  }, [
    selectionScope,
    selectAllSnapshot,
    effectiveSearchParams,
    deselectedIds,
    selectedIds,
    extractFilters,
  ]);

  // Bulk action handlers
  const handleBulkArchive = React.useCallback(() => {
    if (selectedCount === 0) return;
    bulkArchiveMutation.mutate(buildBulkPayload());
  }, [selectedCount, bulkArchiveMutation, buildBulkPayload]);

  const handleBulkRun = React.useCallback(async () => {
    if (selectedCount === 0) return;
    try {
      const res = await fetch(`${API_URL}/workflows/test-runs/active-runs`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const count = (data.executions ?? []).length;
        if (count > 0) {
          setActiveRunCount(count);
          setBulkRunConfirmOpen(true);
          return;
        }
      }
    } catch {
      // If the check fails, proceed without warning
    }
    bulkRunMutation.mutate(buildBulkPayload());
  }, [selectedCount, bulkRunMutation, buildBulkPayload]);

  const handleClearSearch = React.useCallback(() => {
    setSearchInput('');
    updateSearch({ search: undefined, page: 1 });
  }, [updateSearch]);

  const handleBulkTag = React.useCallback(() => {
    if (selectedCount === 0 || tagsToAdd.length === 0) return;
    const payload = buildBulkPayload();
    bulkTagMutation.mutate({
      ...payload,
      tagIds: tagsToAdd.map((t) => t.id),
    });
  }, [selectedCount, tagsToAdd, bulkTagMutation, buildBulkPayload]);

  // The toolbar fuses with the table card only when the table actually
  // renders — loading/empty states keep their own standalone chrome.
  const isTableLockup =
    view === 'table' && !workflowsQuery.isLoading && (workflowsQuery.data?.items ?? []).length > 0;

  // Render body
  const renderBody = () => {
    const isLoading = view === 'table' ? workflowsQuery.isLoading : groupedQuery.isLoading;
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      );
    }

    const workflows = workflowsQuery.data?.items ?? [];
    const pagination = workflowsQuery.data?.pagination;
    const groupedItems = groupedQuery.data?.data ?? [];
    const itemCount = view === 'table' ? workflows.length : groupedItems.length;

    if (itemCount === 0 && !hasActiveFilters && !searchParams.search) {
      const empty =
        scriptKind === 'seed'
          ? {
              title: 'Create your first seed script',
              body: 'Seed scripts provision shared test data before your suites run. Build one to get started.',
              cta: 'New Seed Script',
              onPress: () => setSeedDialogOpen(true),
            }
          : scriptKind === 'teardown'
            ? {
                title: 'Create your first teardown script',
                body: 'Teardown scripts clean up seed data after your suites complete. Build one to get started.',
                cta: 'New Teardown Script',
                onPress: () => setTeardownDialogOpen(true),
              }
            : {
                title: 'Create your first flow',
                body: 'Flows let you define multi-step test scenarios. Build your first one to start testing user journeys automatically.',
                cta: 'New Flow',
                onPress: handleCreateFlow,
              };
      return (
        <Card className="p-8 text-center border-dashed">
          <Flex direction="column" gap="md" align="center">
            <Text weight="medium" size="lg">
              {empty.title}
            </Text>
            <Text size="sm" className="text-muted-foreground max-w-md">
              {empty.body}
            </Text>
            <Button
              variant="primary"
              iconLeft={<Plus size={18} weight="bold" />}
              onPress={empty.onPress}
              isDisabled={scriptKind === null && createWorkflowMutation.isPending}
            >
              {empty.cta}
            </Button>
          </Flex>
        </Card>
      );
    }

    if (itemCount === 0) {
      return (
        <Card className="p-8 text-center border-dashed">
          <Flex direction="column" gap="md" align="center">
            <Text weight="medium">No workflows match your filters</Text>
            <Text size="sm" className="text-muted-foreground">
              Try adjusting your search or filter criteria
            </Text>
            <Button variant="outline" onPress={handleResetFilters}>
              Clear filters
            </Button>
          </Flex>
        </Card>
      );
    }

    if (view === 'grouped') {
      return (
        <FlowTagSections
          items={groupedItems}
          capped={groupedQuery.data?.capped ?? false}
          workflowTimelineEntries={workflowTimelineEntries}
          diagnosedCategoryByWorkflow={diagnosedCategoryByWorkflow}
          openIssuesByWorkflow={openIssuesByWorkflow}
          latestRunByWorkflow={latestRunByWorkflow}
          isRunsFetching={workflowRunsQuery.isFetching}
          membersByUserId={membersByUserId}
          actionHandlers={actionHandlers}
          onToggleFavorite={toggleFavorite}
          onOpen={handleOpenFlow}
          isSectionCollapsed={isSectionCollapsed}
          onSectionCollapsedChange={setSectionCollapsed}
        />
      );
    }

    return (
      <FlowTableView
        workflows={workflows}
        pagination={pagination}
        workflowTimelineEntries={workflowTimelineEntries}
        diagnosedCategoryByWorkflow={diagnosedCategoryByWorkflow}
        openIssuesByWorkflow={openIssuesByWorkflow}
        latestRunByWorkflow={latestRunByWorkflow}
        membersByUserId={membersByUserId}
        buildingWorkflowIds={importStream.buildingWorkflowIds}
        newWorkflowIds={importStream.newWorkflowIds}
        isRunsFetching={workflowRunsQuery.isFetching}
        actionHandlers={actionHandlers}
        onToggleFavorite={toggleFavorite}
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        onRowClick={handleRowClick}
        getRowHref={(workflow) => flowHref(workflow.id)}
        onPageChange={handlePageChange}
        sortBy={searchParams.sortBy}
        sortOrder={searchParams.sortOrder}
        onSortChange={handleSortChange}
        containerClassName={isTableLockup ? 'rounded-t-none filter-lockup-table' : undefined}
      />
    );
  };

  return (
    <PageLayout>
      <PageHeader
        icon={<FlowArrow size={24} />}
        title="Flows"
        description={(() => {
          const totalCount =
            view === 'table'
              ? workflowsQuery.data?.pagination?.totalItems
              : groupedQuery.data?.data.length;
          const allEntries = Object.values(workflowTimelineEntries).flat();
          const passCount = allEntries.filter(
            (e) => e.tone === 'success' || e.tone === 'info'
          ).length;
          const total = allEntries.length;
          if (total === 0 || totalCount === null || totalCount === undefined) {
            return 'Manage workflow tests for your organization.';
          }
          const pct = Math.round((passCount / total) * 100);
          return `${totalCount} flow${totalCount === 1 ? '' : 's'} · ${pct}% pass rate over the last 14 days`;
        })()}
        actions={
          <Flex gap="sm">
            {scriptKind === null ? (
              <>
                <Button
                  variant="outline"
                  iconLeft={<UploadSimple size={18} weight="bold" />}
                  onPress={() => setImportDialogOpen(true)}
                >
                  Import
                </Button>
                <Button
                  ref={createFlowGuide.ref}
                  variant="primary"
                  iconLeft={<Plus size={18} weight="bold" />}
                  onPress={handleCreateFlow}
                  isDisabled={createWorkflowMutation.isPending}
                >
                  New Flow
                </Button>
              </>
            ) : scriptKind === 'seed' ? (
              <Button
                variant="primary"
                iconLeft={<Plus size={18} weight="bold" />}
                onPress={() => setSeedDialogOpen(true)}
              >
                New Seed Script
              </Button>
            ) : (
              <Button
                variant="primary"
                iconLeft={<Plus size={18} weight="bold" />}
                onPress={() => setTeardownDialogOpen(true)}
              >
                New Teardown Script
              </Button>
            )}
          </Flex>
        }
      />

      <UnderlineTabs
        selectedId={
          searchParams.tab === 'seed-scripts'
            ? 'seed-scripts'
            : searchParams.tab === 'teardown-scripts'
              ? 'teardown-scripts'
              : 'flows'
        }
        onSelectionChange={(id) => updateSearch({ tab: id === 'flows' ? undefined : id, page: 1 })}
        layoutId="flows-tabs"
        className="mb-4"
      >
        <UnderlineTab id="flows">Flows</UnderlineTab>
        <UnderlineTab id="seed-scripts">
          <Flex align="center" gap="xs">
            <Plant size={14} />
            Seed Scripts
          </Flex>
        </UnderlineTab>
        <UnderlineTab id="teardown-scripts">
          <Flex align="center" gap="xs">
            <Broom size={14} />
            Teardown Scripts
          </Flex>
        </UnderlineTab>
      </UnderlineTabs>

      {/* The same table/filters/views/bulk-actions render for every tab; the
          active tab only changes the injected flowType (effectiveSearchParams). */}
      <>
        {showImportBanner && (
          <FlowListImportBanner
            importStream={importStream}
            onViewErrors={() => setShowErrorsDrawer(true)}
            onDismiss={() => setActiveImportId(null)}
          />
        )}

        {view === 'table' && (
          <BulkActionBar
            selectedCount={selectedCount}
            itemLabel="flow"
            selectionMode={resolvedSelectionMode}
            deselectedCount={deselectedCount}
            totalItems={totalItems}
            onSelectAll={canSelectAllResults ? handleSelectAllResults : undefined}
            onClearSelection={clearSelection}
            actions={
              [
                {
                  label: 'Tag',
                  icon: <Tag size={14} />,
                  variant: 'outline',
                  onPress: () => setBulkTagDialogOpen(true),
                },
                {
                  label: 'Run all',
                  loadingLabel: 'Starting...',
                  icon: <Play size={14} />,
                  variant: 'outline',
                  onPress: handleBulkRun,
                  isLoading: bulkRunMutation.isPending,
                },
                {
                  key: 'set-status',
                  render: (
                    <MenuTrigger>
                      <Button
                        variant="outline"
                        size="sm"
                        isDisabled={
                          bulkStatusMutation.isPending ||
                          bulkPauseMutation.isPending ||
                          bulkPromotionMutation.isPending
                        }
                      >
                        {bulkStatusMutation.isPending ||
                        bulkPauseMutation.isPending ||
                        bulkPromotionMutation.isPending
                          ? 'Updating...'
                          : 'Set status'}
                      </Button>
                      <Menu
                        onAction={(key) => {
                          if (selectedCount === 0) return;
                          const statusMap: Record<string, WorkflowStatus> = {
                            approve: 'approved',
                            draft: 'draft',
                          };
                          const status = statusMap[key as string];
                          if (status) {
                            bulkStatusMutation.mutate({ ...buildBulkPayload(), status });
                            return;
                          }
                          if (eligibilityModel) {
                            // Eligibility model: Pause / Unpause via the pause toggle.
                            if (key === 'pause' || key === 'unpause') {
                              bulkPauseMutation.mutate({
                                ...buildBulkPayload(),
                                paused: key === 'pause',
                              });
                            }
                          } else if (key === 'promote' || key === 'pause') {
                            // Promotion model: Promote / Pause via the promotion axis.
                            bulkPromotionMutation.mutate({
                              ...buildBulkPayload(),
                              promotion: key === 'promote' ? 'promoted' : 'paused',
                            });
                          }
                        }}
                      >
                        <MenuItem id="approve">Approve</MenuItem>
                        <MenuItem id="draft">Set draft</MenuItem>
                        {!eligibilityModel && <MenuItem id="promote">Promote</MenuItem>}
                        <MenuItem id="pause">Pause</MenuItem>
                        {eligibilityModel && <MenuItem id="unpause">Unpause</MenuItem>}
                      </Menu>
                    </MenuTrigger>
                  ),
                },
                {
                  label: 'Archive',
                  loadingLabel: 'Archiving...',
                  icon: <Trash size={14} />,
                  variant: 'destructive',
                  onPress: handleBulkArchive,
                  isLoading: bulkArchiveMutation.isPending,
                },
              ] satisfies BulkAction[]
            }
          />
        )}

        {/* View controls sit outside the lockup; the toolbar below fuses with
          the table (no chips layer — all primary filters live in the bar) */}
        {/* One wrapper around controls + toolbar + body so ancestor space-y
          stacks can't inject gaps inside the lockup */}
        <div className="mt-6">
          <FlowViewControls
            className="mb-2"
            view={view}
            onViewChange={handleViewChange}
            totalCount={
              view === 'table'
                ? (workflowsQuery.data?.pagination?.totalItems ?? null)
                : (groupedQuery.data?.data.length ?? null)
            }
            onExportCsv={() => window.open(exportUrl, '_blank')}
          />
          <FlowFilterBar
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            searchParams={searchParams}
            onFilterChange={updateSearch}
            tags={tags}
            tagsLoading={tagsLoading}
            filterDialogTrigger={filterDialogTrigger}
            activeFilterChips={activeFilterChips}
            onChipRemove={handleFilterRemove}
            onClearAll={() => {
              handleClearSearch();
              handleResetFilters();
            }}
            showClearAll={hasActiveFilters || !!searchParams.search}
            attached={isTableLockup}
          />

          <div className={isTableLockup ? undefined : 'mt-3'}>{renderBody()}</div>
        </div>

        {/* Import Dialog */}
        <ImportDialog
          isOpen={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          propertyOptions={propertyOptions}
          showPropertySelect={showPropertySelect}
          onCsvSubmit={(file, defaultPropertyId) => {
            uploadImportMutation.mutate({ file, defaultPropertyId });
          }}
          csvIsPending={uploadImportMutation.isPending}
        />

        {/* Archive Dialog */}
        <DialogTrigger
          isOpen={!!workflowToArchive}
          onOpenChange={(open) => {
            if (!open) {
              setWorkflowToArchive(null);
            }
          }}
        >
          <Button className="hidden" />
          <Dialog
            title="Archive workflow"
            description="Archived flows are hidden from lists and won't run in test suites. You can restore them later."
          >
            <DialogContent>
              <Text>
                Archive
                {workflowToArchive ? ` "${workflowToArchive.name || 'Untitled Workflow'}"` : ''}?
              </Text>
            </DialogContent>
            <DialogFooter>
              <Button variant="outline" onPress={() => setWorkflowToArchive(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleConfirmArchive}
                isDisabled={archiveWorkflowMutation.isPending}
              >
                {archiveWorkflowMutation.isPending ? 'Archiving…' : 'Archive'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>

        {/* Bulk Run Confirmation Dialog */}
        <DialogTrigger
          isOpen={bulkRunConfirmOpen}
          onOpenChange={(open) => {
            if (!open) setBulkRunConfirmOpen(false);
          }}
        >
          <Button className="hidden" />
          <Dialog
            title="Run tests"
            description={`${activeRunCount} test run${activeRunCount === 1 ? '' : 's'} currently in progress.`}
          >
            <DialogContent>
              <Text>
                Starting another run will execute both concurrently. Do you want to continue?
              </Text>
            </DialogContent>
            <DialogFooter>
              <Button variant="outline" onPress={() => setBulkRunConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onPress={() => {
                  setBulkRunConfirmOpen(false);
                  bulkRunMutation.mutate(buildBulkPayload());
                }}
                isDisabled={bulkRunMutation.isPending}
              >
                {bulkRunMutation.isPending ? 'Starting...' : 'Run Anyway'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>

        {/* Bulk Tag Dialog */}
        <DialogTrigger
          isOpen={bulkTagDialogOpen}
          onOpenChange={(open) => {
            setBulkTagDialogOpen(open);
            if (!open) {
              setTagsToAdd([]);
            }
          }}
        >
          <Button className="hidden" />
          <Dialog
            title="Add tags to workflows"
            description={`Tags will be added to ${selectedCount} selected workflow${selectedCount === 1 ? '' : 's'}. Existing tags are preserved.`}
          >
            <DialogContent>
              <Flex direction="column" gap="md">
                <TagPicker
                  label="Tags to add"
                  placeholder="Select tags..."
                  tags={tags ?? []}
                  selectedTags={tagsToAdd}
                  onChange={setTagsToAdd}
                  onCreateTag={handleCreateTag}
                  isLoading={tagsLoading}
                />
              </Flex>
            </DialogContent>
            <DialogFooter>
              <Button
                variant="outline"
                onPress={() => {
                  setBulkTagDialogOpen(false);
                  setTagsToAdd([]);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onPress={handleBulkTag}
                isDisabled={bulkTagMutation.isPending || tagsToAdd.length === 0}
              >
                {bulkTagMutation.isPending ? 'Adding tags…' : 'Add tags'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>

        {/* Import Errors Drawer */}
        <ImportErrorsDrawer
          isOpen={showErrorsDrawer}
          onOpenChange={setShowErrorsDrawer}
          importState={importStream}
        />

        {/* Seed / Teardown create dialogs — controlled, opened from
          the tab-aware header CTA and empty state. */}
        <CreateSeedScriptDialog isOpen={seedDialogOpen} onOpenChange={setSeedDialogOpen} />
        <CreateTeardownScriptDialog
          isOpen={teardownDialogOpen}
          onOpenChange={setTeardownDialogOpen}
        />
      </>
    </PageLayout>
  );
}
